/**
 * Una sezione dentro una schermata del banco.
 *
 * Gemello di `apps/backoffice/src/components/sezione.tsx`: stesso vocabolario —
 * `titolo`, `accanto`, `nota`, `azioni` — e altra lingua visiva. I due file non
 * si importano: banco e ufficio non si importano a vicenda, e qui non c'è
 * niente di comune da mettere in `packages/`, c'è la stessa idea detta due
 * volte nelle due lingue.
 *
 * **Questo non avvolge niente, e il gemello d'ufficio sì.** È la sola
 * differenza fra i due, ed è obbligata. Otto degli 11 titoli del banco sono il
 * primo figlio di una `<section className="booth">` o di una
 * `<section className="righe">` — quattro per ciascuna. `.booth` è
 * `display: flex; flex-direction: column`: mettere il loro contenuto dentro un
 * altro elemento trasformerebbe N figli flex in uno, e la cabina luce di
 * `fine-linea` — dove il capo deve riempire l'altezza — collasserebbe. Il
 * frammento lascia il DOM esattamente com'era. `.righe` invece non è un
 * contenitore flex: lì il collasso non è un rischio, ma il frammento resta
 * comunque la scelta coerente, perché un wrapper non aggiungerebbe niente.
 *
 * Il prezzo è il punto di riferimento: al banco una sezione non è un `region`
 * annunciabile. In ufficio quel punto di riferimento serve, perché le schermate
 * sono lunghe e si saltano da una sezione all'altra; al banco una schermata è
 * una postazione sola, si guarda da un metro e si tocca col pollice, e nessuno
 * la naviga per punti di riferimento.
 *
 * La ragione pratica per cui esiste sono i margini. Gli 11 titoli li portavano
 * inline in tre varianti — e le varianti erano **due decisioni**, non tre: il
 * distacco sotto al titolo, uguale ovunque, e l'aria sopra un blocco che non è
 * il primo del suo contenitore. La seconda vive in `distanziata`, la prima nel
 * CSS. Il solo pixel che si muove è il `marginBottom: 8` di `fine-linea.tsx`,
 * che diventa 10.
 */

import type { ReactNode } from 'react';

export interface PropsSezione {
  /**
   * `ReactNode` e non `string`: il titolo di `fine-linea.tsx` è
   * `{cliente} · ordine {ordine} {urgente && '· urgente'}`, e con una stringa
   * quel sito — il più importante del banco — non sarebbe migrabile.
   */
  readonly titolo: ReactNode;
  /** Un dato che qualifica il titolo, sulla sua stessa riga. */
  readonly accanto?: ReactNode;
  /** Una riga che spiega, sotto al titolo. Assente = nessun paragrafo. */
  readonly nota?: ReactNode;
  /** Il gesto che riguarda questa sezione. */
  readonly azioni?: ReactNode;
  /**
   * Aria sopra il titolo, per un blocco che non è il primo del suo contenitore.
   * Sono i tre punti che oggi scrivono `margin: '18px 0 10px'`: quel 18 in cima
   * non è un margine diverso, è una decisione diversa — «qui comincia un altro
   * blocco» — e appiattirlo sui 10 degli altri toglierebbe l'unico segno che
   * separa due elenchi dentro la stessa colonna.
   */
  readonly distanziata?: boolean;
  readonly children: ReactNode;
}

export function Sezione({
  titolo,
  accanto,
  nota,
  azioni,
  distanziata = false,
  children,
}: PropsSezione) {
  return (
    // Frammento, non `<section>`: vedi il commento in testa al file.
    <>
      {/*
        Titolo e azioni sono fratelli, non annidati: un bottone dentro un `h2`
        finisce nel nome accessibile del titolo, e chi ascolta si sente leggere
        «La postazione Riprova» come se fosse una cosa sola.
      */}
      <div className={`sezione__riga${distanziata ? ' sezione__riga--staccata' : ''}`}>
        <h2 className="gesto__k sezione__k">{titolo}</h2>
        {accanto}
        {azioni}
      </div>
      {nota && <p className="sezione__nota">{nota}</p>}
      {children}
    </>
  );
}
